import React from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import VoucherLookupPopup from './components/VoucherLookupPopup';
import Popup from './components/Popup';
import AppShell from './app/AppShell';
import apiClient from './shared/api/client';
import DashboardPage from './features/dashboard/pages/DashboardPage';
import DirectoryPage from './pages/DirectoryPage';
import FamilyDetailPage from './pages/FamilyDetailPage';
import StudentDetailPage from './pages/StudentDetailPage';
import InviteAcceptPage from './features/auth/pages/InviteAcceptPage';
import LocationsPage from './features/locations/pages/LocationsPage';
import LoginPage from './features/auth/pages/LoginPage';
import ProfilePage from './features/profile/pages/ProfilePage';
import SetPasswordPage from './features/auth/pages/SetPasswordPage';
import RatesPage from './features/rates/pages/RatesPage';
import UsersPage from './features/users/pages/UsersPage';
import VouchersPage from './features/vouchers/pages/VouchersPage';
import { loginPathWithReturn, safeReturnPath } from './utils/authRedirect';

function isAuthenticated() {
  return !!localStorage.getItem('token');
}



function PrivateRoute({ children }) {
  const location = useLocation();
  if (!isAuthenticated()) {
    return <Navigate to={loginPathWithReturn(location.pathname + location.search)} replace />;
  }
  return children;
}

function AuthedLoginRedirect() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const ret = safeReturnPath(params.get('returnUrl'));
  if (ret) return <Navigate to={ret} replace />;
  return <Navigate to="/dashboard" replace />;
}

function CatchAllRedirect({ authed }) {
  const location = useLocation();
  if (authed) return <Navigate to="/dashboard" replace />;
  return <Navigate to={loginPathWithReturn(location.pathname + location.search)} replace />;
}
function PopupContextProvider({ children }) {
  const [popup, setPopup] = React.useState({ open: false, message: '', type: 'success' });
  const showPopup = (message, type = 'success') => {
    setPopup({ open: true, message, type });
    setTimeout(() => setPopup(p => ({ ...p, open: false })), 3000);
  };
  return (
    <PopupContext.Provider value={showPopup}>
      {children}
      <Popup open={popup.open} message={popup.message} type={popup.type} />
    </PopupContext.Provider>
  );
}

export const PopupContext = React.createContext(() => {});

function App() {
  const location = useLocation();
  const authed = isAuthenticated();
  const hideNav = !authed || ['/login', '/set-password', '/invite-accept'].includes(location.pathname);
  const [user, setUser] = React.useState(null);

  React.useEffect(() => {
    if (authed) {
      apiClient('/api/auth/me')
        .then(res => res.json())
        .then(data => setUser(data))
        .catch(() => setUser(null));
    } else {
      setUser(null);
    }
  }, [authed]);

  const [showVoucherLookup, setShowVoucherLookup] = React.useState(false);

  React.useEffect(() => {
    if (!authed) return undefined;
    const onKeyDown = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      if (e.key.toLowerCase() !== 'k') return;
      const el = e.target;
      if (el instanceof HTMLElement) {
        const { tagName } = el;
        if (tagName === 'INPUT' || tagName === 'TEXTAREA' || tagName === 'SELECT' || el.isContentEditable)
          return;
      }
      e.preventDefault();
      setShowVoucherLookup((open) => !open);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [authed]);

  if (authed && location.pathname === '/login') {
    return <AuthedLoginRedirect />;
  }

  if (authed && location.pathname === '/') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <AppShell
      hideNav={hideNav}
      user={user}
      onVoucherLookupOpen={() => setShowVoucherLookup(true)}
      overlay={<VoucherLookupPopup open={showVoucherLookup} onClose={() => setShowVoucherLookup(false)} />}
    >
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/set-password" element={<SetPasswordPage />} />
        <Route path="/invite-accept" element={<InviteAcceptPage />} />
        <Route path="/dashboard" element={<PrivateRoute><DashboardPage /></PrivateRoute>} />
        <Route path="/users" element={<PrivateRoute><UsersPage /></PrivateRoute>} />
        <Route path="/families" element={<PrivateRoute><DirectoryPage /></PrivateRoute>} />
        <Route path="/students" element={<PrivateRoute><DirectoryPage /></PrivateRoute>} />
        <Route path="/families/:id" element={<PrivateRoute><FamilyDetailPage /></PrivateRoute>} />
        <Route path="/students/:id" element={<PrivateRoute><StudentDetailPage /></PrivateRoute>} />
        <Route path="/directory" element={<PrivateRoute><Navigate to="/students" replace /></PrivateRoute>} />
        <Route path="/locations" element={<PrivateRoute><LocationsPage /></PrivateRoute>} />
        <Route path="/rates" element={<PrivateRoute><RatesPage /></PrivateRoute>} />
        <Route
          path="/settings"
          element={
            <PrivateRoute>
              <Navigate to="/rates" replace />
            </PrivateRoute>
          }
        />
        <Route path="/vouchers" element={<PrivateRoute><VouchersPage /></PrivateRoute>} />
        <Route
          path="/voucher-status"
          element={
            <PrivateRoute>
              <Navigate to="/vouchers" replace />
            </PrivateRoute>
          }
        />
        <Route path="/profile" element={<PrivateRoute><ProfilePage user={user} setUser={setUser} /></PrivateRoute>} />
        <Route path="*" element={<CatchAllRedirect authed={authed} />} />
      </Routes>
    </AppShell>
  );
}

export default function AppWithRouter() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <PopupContextProvider>
        <App />
      </PopupContextProvider>
    </Router>
  );
}
